/**
 * Phone — a player's page (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile
 * Crew.dc.html:364-423`). AA phase 5 §J.
 *
 * Sticky bar `◂ PARTY` · avatar · name · role chip; a horizontally scrolling
 * 44px tab strip that UNDERLINES instead of boxing; scores as a two-column
 * grid; GOTY chips at 60×90. Read-only; every tile opens the game.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { memberVM } from '../store/selectors.ts';
import type { ArtCand, GameId } from '../lib/types.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

function ScoreTile({ id, n, scTxt, scClr, ini, fall, coverCands }: { id: GameId; n: string; scTxt: string; scClr: string; ini: string; fall: string; coverCands: ArtCand[] }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(id + 'p', coverCands);
  return (
    <div onClick={() => openGame(id)} style={{ position: 'relative', minWidth: 0, aspectRatio: '2/3', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={ini} hasCover={art.hasCover} cover={art.cover} />
      <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, background: 'var(--gt-ink)', borderLeft: '1px solid ' + scClr, borderBottom: '1px solid ' + scClr, padding: '7px 9px 6px', zIndex: 2 }}>
        <span style={{ fontFamily: PIXEL, fontSize: 15, color: scClr, transform: 'translateY(0.13em)' }}>{scTxt}</span>
        <span style={{ fontFamily: MONO, fontSize: 6.5, letterSpacing: '.12em', color: 'var(--gt-muted-2)' }}>SCORE</span>
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '18px 10px 9px', background: 'linear-gradient(180deg,transparent,rgba(5,5,8,.94))', fontWeight: 600, fontSize: 12.5, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {n}
      </div>
    </div>
  );
}

function Chip({ id, n, ini, fall, coverCands }: { id: GameId; n: string; ini: string; fall: string; coverCands: ArtCand[] }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const art = useArt(id + 'p', coverCands);
  return (
    <div onClick={() => openGame(id)} data-tip={n} style={{ position: 'relative', width: 60, height: 90, flex: 'none', background: fall, overflow: 'hidden', border: '1px solid var(--gt-line)', cursor: 'pointer' }}>
      <CoverArt style={{ position: 'absolute', inset: 0 }} fs={8} ini={ini} hasCover={art.hasCover} cover={art.cover} />
    </div>
  );
}

export default function Member(): JSX.Element {
  const s = useQd();
  const v = memberVM(s);

  // A player's page fetches its own art. What it draws is THEIR scores and
  // THEIR GOTY chips — games that may be on none of your surfaces at all, and
  // the GOTY half arrives from `memGoty` after the page is already open — so
  // nothing else here has ever asked `/api/steam/items` about them.
  useQueueArt([...v.scoreRows.map((r) => r.id), ...v.memTiers.flatMap((t) => t.chips.map((c) => c.id))]);

  return (
    <div data-screen-label="Player page" style={{ display: 'flex', flexDirection: 'column', flex: 1, paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-panel)', position: 'sticky', top: 0, zIndex: 20 }}>
        <div onClick={() => s.nav('crew')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 11px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: MONO, fontSize: '9.5px', letterSpacing: '.05em', cursor: 'pointer', boxSizing: 'border-box' }}>
          ◂ PARTY
        </div>
        <div style={{ position: 'relative', width: 30, height: 30, flex: 'none', background: v.hueBg, display: 'grid', placeItems: 'center', fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-ink)', overflow: 'hidden' }}>
          {v.ini}
          {v.hasAv ? <div style={{ position: 'absolute', inset: 0, background: v.avBg }} /> : null}
        </div>
        <span style={{ fontFamily: PIXEL, fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>{v.nameUp}</span>
        <span style={{ fontFamily: MONO, fontSize: 8, letterSpacing: '.08em', color: v.roleClr, border: '1px solid ' + v.roleClr, padding: '3px 6px', flex: 'none' }}>{v.roleLbl}</span>
      </div>
      <div data-hs="1" style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid var(--gt-line)', fontFamily: MONO, fontSize: 10 }}>
        {v.tabs.map((t) => (
          <div key={t.key} onClick={() => s.setMemTab(t.key)} style={{ height: 44, display: 'flex', alignItems: 'center', padding: '0 16px', color: t.clr, borderBottom: '2px solid ' + t.bdB, cursor: 'pointer', whiteSpace: 'nowrap', flex: 'none', boxSizing: 'border-box' }}>
            {t.l}
          </div>
        ))}
      </div>
      <div style={{ padding: '14px 14px 26px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {v.showScores ? (
          <>
            <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>WHAT THEY PLAYED</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
              {v.scoreRows.map((r) => (
                <ScoreTile key={r.key} id={r.id} n={r.n} scTxt={r.scTxt} scClr={r.scClr} ini={r.ini} fall={r.fall} coverCands={r.coverCands} />
              ))}
            </div>
            {v.scoresEmpty ? <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)', padding: '20px 0', textAlign: 'center' }}>NOTHING SCORED YET</div> : null}
          </>
        ) : (
          <>
            <span style={{ fontFamily: PIXEL, fontSize: 9, color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>THEIR GOTY</span>
            {v.memTiers.map((row) => (
              <div key={row.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ width: 22, flex: 'none', fontFamily: PIXEL, fontSize: 12, color: row.clr, transform: 'translateY(0.13em)', paddingTop: 4 }}>{row.t}</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {row.chips.map((c) => (
                    <Chip key={c.key} id={c.id} n={c.n} ini={c.ini} fall={c.fall} coverCands={c.coverCands} />
                  ))}
                </div>
              </div>
            ))}
            {v.gotyEmpty ? <div style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)', padding: '10px 0', textAlign: 'center' }}>{v.gotyEmptyTxt}</div> : null}
          </>
        )}
      </div>
    </div>
  );
}
